import type {ReactElement} from 'react';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {Dropdown} from '@Components/dropdown-v2';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import type {OverlayInjectedProps} from 'react-bootstrap/Overlay';
import type {BlockCategoryItemProps} from './block-category-item.types';
import styles from '../../email-maker-block-manager.module.scss';

const INTRO_POPUP_OFFSET_LEFT = 41;
const INTRO_POPUP_ARROW_OFFSET = 31;

export function BlockCategoryItem({
  icon,
  label,
  isSelected,
  blockClasses = '',
  textClasses = '',
  showActivityIndicator = false,
  introPopupContent,
  introPopupShowDelay,
  introPopupHideDelay,
  isAnyPanelOpen = false,
  onClick,
  wrapperClasses,
  id,
}: BlockCategoryItemProps): ReactElement {
  const labelAndIcon = (
    <div id={id} className={`${styles.block} ${blockClasses} ${isSelected ? styles.selected : ''}`}>
      {showActivityIndicator ? <PulsatingDot containerClassname={styles.activityIndicator} /> : null}
      <Icon icon={icon} size={IconSize.SIZE_ICON_24} className={textClasses} />
      <Text val={label} size={TextSize.XXSMALL} bold={isSelected} className={textClasses} />
    </div>
  );

  const buildOverlay = (injectedProps: OverlayInjectedProps): ReactElement => {
    if (!introPopupContent) {
      return <div ref={injectedProps.ref} style={injectedProps.style} />;
    }

    const popup = (
      <div className={styles.introPopup}>
        {introPopupContent.image}
        {introPopupContent.description}
      </div>
    );

    return (
      <div ref={injectedProps.ref} style={injectedProps.style} className={styles.introPopupContainer}>
        <Dropdown
          open
          popup={popup}
          alignment={ALIGNMENT_TYPE.RIGHT_START}
          selector={<div />}
          position={DROPDOWN_POSITION.ABSOLUTE}
          useDefaultArrow
          leftOffset={INTRO_POPUP_OFFSET_LEFT}
          arrowOffset={INTRO_POPUP_ARROW_OFFSET}
          popUpHasCustomWidth
          popUpHasCustomHeight
          arrowClasses={styles.popupPanelArrow}
          popupClassName={`${styles.popupPanel} ${styles.dynamicHeight}`}
        />
      </div>
    );
  };

  const tile = (
    <OverlayTrigger trigger={['hover', 'focus']} delay={{show: introPopupShowDelay, hide: introPopupHideDelay}} overlay={buildOverlay} show={isAnyPanelOpen ? false : undefined}>
      {labelAndIcon}
    </OverlayTrigger>
  );

  if (onClick) {
    return (
      <ClickableDiv className={wrapperClasses} onClick={onClick}>
        {tile}
      </ClickableDiv>
    );
  }

  return <div className={wrapperClasses}>{tile}</div>;
}
